import React, { useRef, useState } from 'react'
import Menu, { MenuProps } from 'components/menu'
import {
  Palette,
  Pencil,
  Arrow,
  Delete,
  Visible,
  Lock,
  Asterisk,
  Group,
  Colour,
  Copy,
  Zip,
  SizeWidthIncrease,
  FormWidthFull,
  FormWidthHalf,
  Variable,
  Tick,
  Unlock,
  AddFilter,
  Filter,
  Cross,
  Join
} from 'components/icons'
import {
  createTableColumnViewAction,
  updateColumn,
  updateColumnFields
} from 'components/spreadsheet/contexts/data/actions'
import { getViewCapabilities } from 'components/spreadsheet/constants/viewCapabilities'
import { useProject } from 'hooks/useProject'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useNewColumn } from 'components/spreadsheet/components/new-column/useNewColumn'
import NewColumnMenu from 'components/spreadsheet/components/new-column'
import {
  IContextMenuState,
  ITableViewColumn,
  ITableViewFilter,
  ITableViewGroup,
  ITableViewSort,
  SelectKindOptions
} from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { getDefaultFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'
import { IDownloadZipProgress, IFilterType, sortDirections } from 'components/spreadsheet/types'
import { useApplicationStore } from 'hooks/application'
import { exportAttachmentsAsZip } from 'components/spreadsheet/helpers/export'
import { getTreeLeafColumnId } from 'components/spreadsheet/helpers/grouping'
import { DEFAULT_FORM_COLUMN_WIDTH } from 'components/spreadsheet/constants/const'
import { MenuIconSpinner, MenuItem, MenuLabel, MenuList, MenuSeparator, MenuSub } from 'components/menu/items'
import { exportWidths } from 'components/spreadsheet/components/column/formatting-fields'
import HeaderFormattingMenu, { HeaderColours } from 'components/spreadsheet/components/menu/views/header-formatting'
import { directionOptions, sortLabel } from 'components/spreadsheet/components/menu/views/sort/directions'
import { PANEL_WIDTH, panelAnchor } from 'components/menu/panel'
import { TableName, openLookups } from 'components/spreadsheet/components/modal/views/tablejoins'
import { useColumnLookup } from 'components/spreadsheet/components/header/LookupIndicator'

interface ColumnMenuProps extends MenuProps {
  setEditColumnMenu: (open: boolean, panel?: 'formula') => void
  setDeleteColumnModal: (open: boolean) => void
}

// The widest the menu grows to fit its rows.
const COLUMN_MENU_MAX_WIDTH = 360

const ColumnMenu: React.FC<ColumnMenuProps> = ({
  id,
  menuState,
  setMenuState,
  width,
  setEditColumnMenu,
  setDeleteColumnModal
}) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData, setFilterMenu, setColourMenu, setGroupMenu, showToolbar } =
    useDataContext()
  const { displayErrorMessage } = useApplicationStore()
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false)
  const [zipProgress, setZipProgress] = useState<IDownloadZipProgress>({ done: 0, total: 0 })

  const capabilities = getViewCapabilities(spreadsheetData.viewDetails.type).columnMenu

  const column = menuState.columnId
    ? spreadsheetData.viewDetails.columns.find((column: ITableViewColumn) => column.publicId === menuState.columnId)
    : undefined

  const treeLeafColumnId = getTreeLeafColumnId(
    spreadsheetData.viewDetails.columns,
    spreadsheetData.userConfiguration.groupSettings,
    spreadsheetData.viewDetails.treeGroupView
  )

  const hideDuplicateColumn = !spreadsheetData.isAdmin || !column || column.isJoined || column.isSynced

  const newColumn = useNewColumn(spreadsheetData, setSpreadsheetData)

  // Open Edit column for a new column to insert before the clicked one, as the header + button does, once
  // this context menu is closed. Anchored to the clicked column's header (captured before the insert shifts
  // the grid): the new column takes that column's position, so the panel opens below where it will appear,
  // rather than at the context menu's right-click location, which can be far off.
  const handleInsertColumn = (sortOrder: number): void => {
    const header = menuState.columnId ? document.getElementById(`column/${menuState.columnId}`) : null
    const rect = header?.getBoundingClientRect()
    const anchor = rect
      ? { top: `${rect.bottom}px`, left: `${rect.left}px` }
      : { top: menuState.top, left: menuState.left }
    setMenuState({ ...menuState, open: false })
    newColumn.open(sortOrder, anchor)
  }

  const handleDuplicateColumn = () => {
    if (!column) {
      displayErrorMessage("This column couldn't be found. Refresh the page and try again.")
      return
    }

    if (hideDuplicateColumn) {
      displayErrorMessage("Only table Owners can duplicate columns, and joined or synced columns can't be duplicated.")
      return
    }

    const { publicId, ...newColumn } = column as ITableViewColumn & {
      kindOptions?: SelectKindOptions
    }
    void publicId
    if (newColumn.kindOptions?.tableOptions) {
      newColumn.kindOptions = {
        ...newColumn.kindOptions,
        tableOptions: { ...newColumn.kindOptions.tableOptions, cachedOptions: undefined }
      }
    }

    // Delete all the properties that are not needed for a new column
    newColumn.isJoined = false
    newColumn.isSynced = false
    newColumn.name = `${newColumn.name} (Copy)`
    newColumn.sortOrder = newColumn.sortOrder + 1
    const payload = { ...newColumn, context: { projectId: project.publicId } }

    createTableColumnViewAction(
      spreadsheetData.viewDetails.publicId,
      payload,
      setSpreadsheetData,
      handleOnSuccess,
      handleOnFailure
    )
  }

  // The header's colours, in their own panel under the column's header, saved as they're picked.
  const [headerMenu, setHeaderMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const openHeaderFormatting = (): void => {
    const header = menuState.columnId ? document.getElementById(`column/${menuState.columnId}`) : null
    const rect = header?.getBoundingClientRect()
    setMenuState({ ...menuState, open: false })
    setHeaderMenu({
      ...INITIAL_CONTEXT_MENU_STATE,
      open: true,
      columnId: menuState.columnId,
      top: rect ? `${rect.bottom}px` : menuState.top,
      left: rect ? `${rect.left}px` : menuState.left
    })
  }
  const headerMenuColumn = spreadsheetData.viewDetails.columns.find((col) => col.publicId === headerMenu.columnId)
  // The colour saves in the order they were made, each sent once the one before it has been answered.
  const colourSaves = useRef<Promise<void>>(Promise.resolve())
  const saveHeaderColours = (patch: Partial<HeaderColours>): void => {
    if (!headerMenuColumn) return
    const columnId = headerMenuColumn.publicId
    // One request a change, so Reset puts both colours back together or not at all, and one at a time, however
    // quickly they're picked: each answer replaces the whole column, so two in flight could arrive out of order and
    // put back a colour just changed.
    colourSaves.current = colourSaves.current.then(
      () =>
        new Promise<void>((resolve) =>
          updateColumnFields(
            project.publicId,
            spreadsheetData,
            setSpreadsheetData,
            columnId,
            patch,
            resolve,
            (error) => {
              handleOnFailure(error)
              resolve()
            }
          )
        )
    )
  }

  // How wide the column is in exports, picked from the column's menu.
  const exportWidth = column?.exportWidth ?? null
  const setExportWidth = (width: number | null): void => {
    if (!column) return
    updateColumn(
      project.publicId,
      'exportWidth',
      spreadsheetData,
      setSpreadsheetData,
      column.publicId,
      width,
      handleOnSuccess,
      handleOnFailure
    )
  }

  const handleOnSuccess = () => {
    setMenuState({ ...menuState, open: false })
  }

  const handleOnFailure = (error: any) => {
    displayErrorMessage(error)
  }

  // The toolbar collapses into a single dropdown on narrow viewports — which is what browser zoom
  // and Windows display magnification produce — so the filter/colour buttons these sub-menus were
  // anchored to may not be in the DOM at all. Fall back to the column menu's own anchor rather than
  // doing nothing, and let Menu clamp the result into the viewport
  // The filter and colour panels open under their toolbar buttons, as they do from the buttons themselves.
  const panelAnchorUnder = (toolbarButtonId: string, width: number = PANEL_WIDTH.lg) => {
    const toolbarButton = document.getElementById(toolbarButtonId)
    return toolbarButton ? panelAnchor(toolbarButton, width) : { top: menuState.top, left: menuState.left }
  }

  const openGroups = () => {
    setGroupMenu({
      open: true,
      ...panelAnchorUnder('group-toolbar-button', PANEL_WIDTH.md),
      right: 'auto',
      bottom: 'auto'
    })
    setMenuState({ ...menuState, open: false })
  }

  // Opens the Conditional formatting panel: with a new rule on this column to fill in, or as it is, to edit.
  const openColours = (withNewRule: boolean) => {
    if (!column) return
    setColourMenu({
      open: true,
      ...panelAnchorUnder('colour-toolbar-button'),
      right: 'auto',
      bottom: 'auto',
      ...(withNewRule && {
        defaultColour: {
          columnId: column.publicId,
          columnName: column.name,
          filterType: getDefaultFilterTypeForColType(column.kind),
          multipleValues: [],
          backgroundColour: '#FFFFFF',
          fontColour: '#333333',
          fontBold: false,
          fontItalic: false,
          fontUnderline: false,
          applyToEntireRow: false,
          value: ''
        }
      })
    })
    setMenuState({ ...menuState, open: false })
  }

  const removeColours = () => {
    setSpreadsheetData({
      type: 'UPDATE_COLOURS',
      colours: spreadsheetData.userConfiguration.colourSettings.filter(
        (setting) => setting.columnId !== menuState.columnId
      )
    })
    setMenuState({ ...menuState, open: false })
  }

  // Opens the Filter panel: with a new filter on this column to fill in, or as it is, to edit.
  const openFilters = (withNewFilter: boolean) => {
    if (!column) return
    setFilterMenu({
      open: true,
      ...panelAnchorUnder('filter-toolbar-button'),
      right: 'auto',
      bottom: 'auto',
      ...(withNewFilter && {
        defaultFilter: {
          columnId: column.publicId,
          columnName: column.name,
          filterType: getDefaultFilterTypeForColType(column.kind),
          value: '',
          multipleValues: [],
          orGroup: 'main'
        }
      })
    })
    setMenuState({ ...menuState, open: false })
  }

  const removeFilters = () => {
    setSpreadsheetData({
      type: 'UPDATE_FILTERS',
      filters: spreadsheetData.userConfiguration.filterSettings.filter(
        (setting: ITableViewFilter) => setting.columnId !== menuState.columnId
      )
    })
    setMenuState({ ...menuState, open: false })
  }

  const columnSorted =
    column &&
    spreadsheetData.userConfiguration.sortSettings.find(
      (sortSetting: ITableViewSort) => sortSetting.columnId === column.publicId
    )
  const filterCount = spreadsheetData.userConfiguration.filterSettings.filter(
    (setting: ITableViewFilter) => setting.columnId === menuState.columnId
  ).length
  const colourCount = spreadsheetData.userConfiguration.colourSettings.filter(
    (setting) => setting.columnId === menuState.columnId
  ).length
  // Something that's on for the column, as its rows under a blue heading.
  const active = (label: string, rows: React.ReactNode[]) => [
    <MenuLabel key={`label-${label}`} active>
      {label}
    </MenuLabel>,
    ...rows
  ]
  // The lookup filling the column, named, for its row's second line.
  const columnLookup = useColumnLookup(column?.publicId, menuState.open && !!column?.isJoined)
  const isGrouped = spreadsheetData.userConfiguration.groupSettings.some(
    (setting: ITableViewGroup) => setting.columnId === menuState.columnId
  )
  const isLocked =
    !!column && spreadsheetData.userConfiguration.rowLockSettings.some((rule) => rule.columnId === column.publicId)
  const formWidth = column?.formWidth ?? DEFAULT_FORM_COLUMN_WIDTH

  const sortBy = (direction: sortDirections) => {
    if (column) {
      if (columnSorted) setSpreadsheetData({ type: 'DELETE_SORT', columnId: column.publicId })
      setSpreadsheetData({ type: 'ADD_SORT', columnId: column.publicId, columnName: column.name })
      if (direction === sortDirections.desc) {
        setSpreadsheetData({ type: 'CHANGE_SORT_DIRECTION', columnId: column.publicId, newDirection: direction })
      }
    }
    handleOnSuccess()
  }

  const toggleGroup = () => {
    if (column) {
      setSpreadsheetData(
        isGrouped
          ? { type: 'DELETE_GROUP', columnId: column.publicId }
          : { type: 'ADD_GROUP', columnId: column.publicId, columnName: column.name }
      )
      handleOnSuccess()
    }
  }

  const toggleLock = () => {
    if (column) {
      const existingLocks = spreadsheetData.userConfiguration.rowLockSettings
      setSpreadsheetData({
        type: 'UPDATE_LOCKS',
        locks: isLocked
          ? existingLocks.filter((rule) => rule.columnId !== column.publicId)
          : [
              ...existingLocks,
              {
                columnId: column.publicId,
                columnName: column.name,
                filterType: IFilterType.all_values,
                value: '',
                multipleValues: [],
                applyToEntireRow: false
              }
            ]
      })
    }
    handleOnSuccess()
  }

  const toggleRequired = () => {
    if (!column) return
    setSpreadsheetData({ type: column.required ? 'UNREQUIRE_COLUMN' : 'REQUIRE_COLUMN', columnId: column.publicId })
    handleOnSuccess()
  }

  const toggleFormWidth = () => {
    if (column) {
      updateColumn(
        project.publicId,
        'formWidth',
        spreadsheetData,
        setSpreadsheetData,
        column.publicId,
        formWidth === 2 ? 1 : 2,
        handleOnSuccess,
        handleOnFailure
      )
    }
  }

  const downloadAttachments = async () => {
    if (downloadingZip || !spreadsheetData || !column) return
    setDownloadingZip(true)
    await exportAttachmentsAsZip(spreadsheetData, setZipProgress, displayErrorMessage, column.publicId)
    setDownloadingZip(false)
  }

  const isAdmin = spreadsheetData.isAdmin
  const filterable = capabilities.filter && showToolbar
  const colourable = capabilities.colour && showToolbar
  const lockable = isAdmin && capabilities.lockColumn && (!column || !column.isSynced)

  // Sorting says what it does for the column's type, as the Sort panel does: A to Z, oldest first.
  const sortRows = () =>
    directionOptions(column?.kind).map((direction) => (
      <MenuItem
        key={`sort-${direction.value}`}
        icon={direction.icon}
        role="menuitemradio"
        aria-checked={columnSorted?.direction === direction.value}
        end={columnSorted?.direction === direction.value ? <Tick className="size-4 text-body" /> : undefined}
        onClick={() => sortBy(direction.value)}
      >
        {sortLabel(direction.label)}
      </MenuItem>
    ))
  const canHide =
    capabilities.hideColumn &&
    showToolbar &&
    (!column?.required || column?.isJoined) &&
    !isGrouped &&
    treeLeafColumnId !== column?.publicId

  // The menu in groups, a line between each that has anything in it: the column itself, adding columns, where
  // its values come from (a formula or a lookup), what's on for it and what could be, its settings for exports
  // and forms, and deleting it.
  const lookupRow = (
    <MenuItem
      key="lookup"
      icon={<Join />}
      onClick={() => {
        setMenuState({ ...menuState, open: false })
        openLookups({
          tableId: spreadsheetData.tableDetails.publicId,
          joinId: spreadsheetData.tableDetails.joins?.find((join) =>
            join.dataColumns.some((data) => data.targetColumnId === column?.publicId)
          )?.publicId
        })
      }}
      data-testid="column-menu-edit-lookup"
    >
      Edit lookup
    </MenuItem>
  )
  // A lookup's or a synced column's values come from elsewhere, so a formula is offered but can't be added,
  // and says why.
  const formulaBlocked = column?.isJoined
    ? 'A lookup column takes its values from its source table, so it can’t have a formula'
    : column?.isSynced
      ? 'A synced column takes its values from its integration, so it can’t have a formula'
      : undefined
  const formulaRow = (
    <MenuItem
      key="formula"
      icon={<Variable />}
      onClick={() => setEditColumnMenu(true, 'formula')}
      disabledReason={formulaBlocked}
      data-testid="column-menu-formula"
    >
      {column?.formulaEnabled ? 'Edit formula' : 'Add formula'}
    </MenuItem>
  )
  const canEditValues = capabilities.editColumn && isAdmin && !!column
  const sections: React.ReactNode[][] = [
    [
      capabilities.editColumn && isAdmin && (
        <MenuItem key="edit" icon={<Pencil />} onClick={() => setEditColumnMenu(true)} data-tour="edit-column-button">
          Edit column
        </MenuItem>
      ),
      // The header's colours, in a panel of their own.
      capabilities.editColumn && isAdmin && column && (
        <MenuItem
          key="header-formatting"
          icon={<Palette />}
          onClick={openHeaderFormatting}
          data-tour="column-header-formatting"
        >
          Header formatting
        </MenuItem>
      )
    ],
    [
      capabilities.insertColumn && isAdmin && (
        <MenuItem
          key="insert"
          icon={<Arrow type="left" />}
          onClick={() => {
            if (menuState.columnNumber !== undefined) handleInsertColumn(menuState.columnNumber)
          }}
        >
          Insert column before
        </MenuItem>
      ),
      capabilities.duplicateColumn && isAdmin && !hideDuplicateColumn && (
        <MenuItem
          key="duplicate"
          icon={<Copy />}
          onClick={() => {
            if (menuState.columnNumber !== undefined) handleDuplicateColumn()
          }}
        >
          Duplicate column
        </MenuItem>
      )
    ],
    // Where the column's values come from, under a blue heading when they come from a formula or a lookup, as
    // what's on below is; a formula to add otherwise.
    // The source table named at the right of the heading, as what the section is about.
    canEditValues && column.isJoined
      ? [
          <div key="label-Lookup" className="flex items-center justify-between gap-3 pr-2">
            <MenuLabel active>Lookup</MenuLabel>
            {columnLookup && (
              <span className="min-w-0 truncate pt-1.5 pb-1 text-xs" data-testid="column-menu-lookup-source">
                <TableName name={columnLookup.joinTableName} />
              </span>
            )}
          </div>,
          lookupRow
        ]
      : [],
    canEditValues && !column.isJoined && !column.isSynced && column.formulaEnabled
      ? active('Formula', [formulaRow])
      : [],
    // What could be: a formula (offered, though not addable, on a lookup or synced column).
    [canEditValues && !column.formulaEnabled && formulaRow],
    // What's on for the column, each under its own blue heading, most important first, with ways to change
    // it and, under a plain ×, to remove it; what isn't is offered in the group below.
    capabilities.group && isGrouped
      ? active('Group', [
          // The Group panel opens from the view row, so only where it shows.
          showToolbar && (
            <MenuItem key="edit-groups" icon={<Group />} onClick={openGroups}>
              Edit groups
            </MenuItem>
          ),
          <MenuItem key="ungroup" icon={<Cross />} onClick={toggleGroup}>
            Remove group
          </MenuItem>
        ])
      : [],
    filterable && filterCount > 0
      ? active('Filter', [
          <MenuItem key="edit-filters" icon={<Filter />} onClick={() => openFilters(false)}>
            Edit filters
          </MenuItem>,
          <MenuItem key="remove-filters" icon={<Cross />} onClick={removeFilters}>
            Remove filters
          </MenuItem>
        ])
      : [],
    capabilities.sort && columnSorted
      ? active('Sort', [
          ...sortRows(),
          <MenuItem
            key="unsort"
            icon={<Cross />}
            onClick={() => {
              if (column) setSpreadsheetData({ type: 'DELETE_SORT', columnId: column.publicId })
              handleOnSuccess()
            }}
          >
            Remove sort
          </MenuItem>
        ])
      : [],
    colourable && colourCount > 0
      ? active('Conditional formatting', [
          <MenuItem key="edit-colours" icon={<Colour />} onClick={() => openColours(false)}>
            Edit rules
          </MenuItem>,
          <MenuItem key="remove-colours" icon={<Cross />} onClick={removeColours}>
            Remove rules
          </MenuItem>
        ])
      : [],
    lockable && isLocked
      ? active('Lock', [
          <MenuItem key="unlock" icon={<Unlock />} onClick={toggleLock}>
            Unlock column
          </MenuItem>
        ])
      : [],
    [
      filterable && filterCount === 0 && (
        <MenuItem key="add-filter" icon={<AddFilter />} onClick={() => openFilters(true)}>
          Add filter
        </MenuItem>
      ),
      ...(capabilities.sort && !columnSorted ? sortRows() : []),
      colourable && colourCount === 0 && (
        <MenuItem key="colour" icon={<Colour />} onClick={() => openColours(true)}>
          Add conditional formatting
        </MenuItem>
      ),
      capabilities.group && !isGrouped && (
        <MenuItem key="group" icon={<Group />} onClick={toggleGroup}>
          Group column
        </MenuItem>
      ),
      canHide && (
        <MenuItem
          key="hide"
          icon={<Visible />}
          onClick={() => {
            if (menuState.columnId !== undefined)
              setSpreadsheetData({ type: 'HIDE_COLUMN', columnId: menuState.columnId })
            handleOnSuccess()
          }}
        >
          Hide column
        </MenuItem>
      ),
      capabilities.downloadAttachments && column?.kind === 'attachment' && (
        <MenuItem key="download" icon={downloadingZip ? <MenuIconSpinner /> : <Zip />} onClick={downloadAttachments}>
          {downloadingZip ? `Downloading ${zipProgress.done} of ${zipProgress.total}` : 'Download attachments'}
        </MenuItem>
      ),
      // With the view's filters and sorts, as a lock is a rule on the view's rows.
      lockable && !isLocked && (
        <MenuItem
          key="lock"
          icon={<Lock />}
          disabledReason={column?.formulaEnabled ? 'Formula columns are automatically locked' : undefined}
          onClick={toggleLock}
        >
          Lock column
        </MenuItem>
      )
    ],
    [
      isAdmin && capabilities.requiredField && column && !column.isJoined && (
        <MenuItem key="required" icon={<Asterisk />} onClick={toggleRequired}>
          {column.required ? 'Make optional' : 'Make required'}
        </MenuItem>
      )
    ],
    // How wide it's set out: in a form, and in an export.
    [
      isAdmin && capabilities.formWidth && column && (
        <MenuItem
          key="form-width"
          // What it will be: one of two columns, or the whole row.
          icon={formWidth === 2 ? <FormWidthHalf /> : <FormWidthFull />}
          onClick={toggleFormWidth}
        >
          {formWidth === 2 ? 'Set form width to 1 column' : 'Set form width to 2 columns'}
        </MenuItem>
      ),
      // How wide the column is when the table is exported, the current width at the row's end.
      capabilities.editColumn && isAdmin && column && (
        <MenuSub
          key="export-width"
          icon={<SizeWidthIncrease />}
          label="Width in exports"
          value={exportWidth === null ? 'Automatic' : `${exportWidth} cm`}
          width={180}
        >
          <MenuList>
            {[null, ...exportWidths(exportWidth)].map((width) => (
              <MenuItem
                key={`export-width-${width ?? 'auto'}`}
                role="menuitemradio"
                aria-checked={exportWidth === width}
                end={exportWidth === width ? <Tick className="size-4 text-body" /> : undefined}
                onClick={() => setExportWidth(width)}
              >
                {width === null ? 'Automatic' : `${width} cm`}
              </MenuItem>
            ))}
          </MenuList>
        </MenuSub>
      )
    ],
    [
      isAdmin && capabilities.deleteColumn && (!column || !column.isSynced) && (
        <MenuItem key="delete" icon={<Delete />} variant="destructive" onClick={() => setDeleteColumnModal(true)}>
          Delete column
        </MenuItem>
      )
    ]
  ]
  const shown = sections.map((rows) => rows.filter(Boolean)).filter((rows) => rows.length > 0)

  return (
    <>
      {/* At its usual width, and wider to fit a long row, such as a lookup's source, rather than cut it short. */}
      <Menu
        id={id}
        menuState={menuState}
        setMenuState={setMenuState}
        width={Math.max(width ?? 0, COLUMN_MENU_MAX_WIDTH)}
        minWidth={width}
      >
        <MenuList>
          {shown.map((rows, index) => (
            <React.Fragment key={index}>
              {index > 0 && <MenuSeparator />}
              {rows}
            </React.Fragment>
          ))}
        </MenuList>
      </Menu>
      <NewColumnMenu id="insert-column-menu" newColumn={newColumn} />
      {headerMenu.open && headerMenuColumn && (
        <HeaderFormattingMenu
          id="header-formatting-menu"
          menuState={headerMenu}
          setMenuState={setHeaderMenu}
          column={headerMenuColumn}
          defaults={{
            headerBackgroundColor: project.defaultHeaderBackgroundColor,
            headerTextColor: project.defaultHeaderTextColor
          }}
          onChange={saveHeaderColours}
        />
      )}
    </>
  )
}

export default ColumnMenu
